import { useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';

import { useBootstrap } from '@/lib/api/hooks';
import { useAuth } from '@/lib/auth/AuthProvider';

/**
 * Which accounts this portal serves. RequireAuth wraps every signed-in route
 * in it, outside its own session check.
 *
 * This is the System Labs portal's version. An account created by an
 * rx-store storefront checkout (`bootstrap.storefront` set) belongs to the
 * store's own patient portal: it gets a neutral notice pointing there, this
 * browser's session is signed out, and nothing of this portal renders.
 * System Labs patients (`storefront` null or absent) pass straight through.
 *
 * The storefront build swaps this module for
 * src/flavors/rxstore/RxAccountGate.tsx (vite.config.ts).
 */

const STOREFRONT_PORTAL_URL = (
  import.meta.env.VITE_STOREFRONT_PORTAL_URL || 'https://app.rx-store.com'
).replace(/\/+$/, '');
const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,39}$/;

/** Set once a storefront account is detected, so the notice outlives the
 *  sign-out (after which RequireAuth would otherwise send it to /login). */
type Hold = { userId: string; slug: string | null };
let hold: Hold | null = null;
const listeners = new Set<() => void>();

function setHold(next: Hold | null) {
  if (hold === next) return;
  hold = next;
  listeners.forEach((listener) => listener());
}

function useHold(): Hold | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => hold,
  );
}

export function AccountScopeGate({ children }: { children: ReactNode }) {
  const { session, signOut } = useAuth();
  const current = useHold();
  const userId = session?.user.id ?? null;
  // The one place a held store account is signed out: right after
  // SignedInScope sets the hold, and again if the same account signs back
  // in in this tab. Local only: patients share one auth project across both
  // portals, and a global sign-out would also end their sessions in the
  // store's portal. Once per signed-in stretch.
  const heldUserId = current && userId && userId === current.userId ? userId : null;
  const signingOut = useRef<string | null>(null);
  useEffect(() => {
    if (!heldUserId) {
      signingOut.current = null;
      return;
    }
    if (signingOut.current === heldUserId) return;
    signingOut.current = heldUserId;
    void signOut({ scope: 'local' });
  }, [heldUserId, signOut]);

  if (current && (userId === null || userId === current.userId)) {
    return <ManagedElsewhereNotice slug={current.slug} />;
  }
  // Bootstrap needs a session; without one RequireAuth's own check runs.
  if (!userId) return <>{children}</>;
  return <SignedInScope userId={userId}>{children}</SignedInScope>;
}

function SignedInScope({ userId, children }: { userId: string; children: ReactNode }) {
  const storefront = useBootstrap()?.data?.storefront ?? null;
  const slug = storefront && SLUG_RE.test(storefront.slug) ? storefront.slug : null;

  useEffect(() => {
    if (!storefront) {
      // Another account signed in after a notice: drop the stale hold.
      if (hold && hold.userId !== userId) setHold(null);
      return;
    }
    if (hold?.userId === userId) return; // already held
    // AccountScopeGate shows the notice and signs this session out.
    setHold({ userId, slug });
  }, [storefront, slug, userId]);

  if (storefront) return <ManagedElsewhereNotice slug={slug} />;
  return <>{children}</>;
}

/** Deliberately unbranded: no house name, logo, typeface or support
 *  contact, and the tab title says only "Patient Portal". */
function ManagedElsewhereNotice({ slug }: { slug: string | null }) {
  useEffect(() => {
    const previous = document.title;
    document.title = 'Patient Portal';
    return () => {
      document.title = previous;
    };
  }, []);

  const href = `${STOREFRONT_PORTAL_URL}/login${
    slug ? `?store=${encodeURIComponent(slug)}` : ''
  }`;

  return (
    <main
      className="min-h-screen flex items-center justify-center px-15 bg-white text-black"
      style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif' }}
    >
      <div className="max-w-form w-full flex flex-col gap-20">
        <h1 className="text-28 leading-110 font-normal">
          Your account is managed in your store&rsquo;s patient portal
        </h1>
        <p className="text-16 leading-135 text-[#5E5E5E]">
          You bought your treatment through a store, so your orders, treatments
          and visits are in that store&rsquo;s patient portal. Sign in there
          with the same email.
        </p>
        <a
          href={href}
          className="self-start inline-flex items-center justify-center h-48 px-25 bg-black text-white text-15 hover:bg-[#252525] focus-visible:outline-none focus-visible:shadow-focus"
        >
          Go to your patient portal
        </a>
      </div>
    </main>
  );
}
